import React from "react";
import PropTypes from "prop-types";
import { Button } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getAuthCredentials } from "../../utils/auth";

export default function SalesInventoryExport({ dateRange }) {
  const { token, posId } = getAuthCredentials();

  const handleExport = async () => {
    const { data } = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/v1/api/sales/inventory-report`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          posId,
          startDate: dayjs(dateRange[0]).format("YYYY-MM-DDTHH:mm:ss"),
          endDate: dayjs(dateRange[1]).format("YYYY-MM-DDTHH:mm:ss"),
          page: 1,
          limit: 1e6,
        },
      }
    );

    const {
      menuSummary = [],
      inventorySummary = [],
      currentPredictedInventory = [],
      previousPredictedInventory = [],
      currentActualInventoryPerMenu = [],
    } = data;

    const perMenuPredCurr = {};
    for (const m of currentPredictedInventory || []) {
      const byInv = {};
      for (const ing of m.predictedInventory || []) {
        byInv[ing.inventory] = {
          qty: Number(ing.qty || 0),
          unit: ing.unit || "",
        };
      }
      perMenuPredCurr[m.menuName] = byInv;
    }

    const perMenuActCurr = {};
    for (const m of currentActualInventoryPerMenu || []) {
      const byInv = {};
      for (const ing of m.actualInventory || []) {
        const name = ing.inventory;
        if (!byInv[name])
          byInv[name] = { qty: 0, unit: ing.unit || "", cost: 0 };
        byInv[name].qty += Number(ing.qty || 0);
        byInv[name].cost += Number(ing.cost || 0);
        if (!byInv[name].unit && ing.unit) byInv[name].unit = ing.unit;
      }
      perMenuActCurr[m.menuName || "Unattributed"] = byInv;
    }

    const aggPred = (arr = []) => {
      const byId = {};
      const byName = {};
      for (const m of arr) {
        for (const ing of m.predictedInventory || []) {
          const key = ing.inventoryId || ing.inventory;
          if (!byId[key])
            byId[key] = { qty: 0, name: ing.inventory, unit: ing.unit };
          byId[key].qty += Number(ing.qty || 0);
          byName[ing.inventory] =
            (byName[ing.inventory] || 0) + Number(ing.qty || 0);
        }
      }
      return { byId, byName };
    };
    const prevAgg = aggPred(previousPredictedInventory || []);
    const currAgg = aggPred(currentPredictedInventory || []);

    // 2) Create a new workbook
    const wb = new ExcelJS.Workbook();
    wb.creator = "Your App";
    wb.created = new Date();

    const ws1 = wb.addWorksheet("Menu Breakdown", {
      views: [{ state: "frozen", ySplit: 3 }],
      properties: { defaultColWidth: 15 },
    });

    const title = `Inventory Usage Report (${dayjs(dateRange[0]).format(
      "MMM D, YYYY"
    )} – ${dayjs(dateRange[1]).format("MMM D, YYYY")})`;
    ws1.mergeCells("A1:F1");
    ws1.getCell("A1").value = title;
    ws1.getCell("A1").font = { size: 14, bold: true };
    ws1.getCell("A1").alignment = { horizontal: "center" };

    ws1.addRow([]);

    const header1 = [
      "Menu Item",
      "Unit",
      "Ingredient",
      "Qty Used",
      "Predicted Used",
      "Cost (₱)",
    ];
    ws1.addRow(header1);
    const headerRow1 = ws1.getRow(3);
    headerRow1.font = { bold: true };
    headerRow1.alignment = { horizontal: "center" };
    headerRow1.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFEFEFEF" },
      };
    });

    const addBorderAll = (row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    };

    const fmtQty = "#,##0.###";
    const fmtPeso = "#,##0.00";

    menuSummary.forEach((menu) => {
      const mr = ws1.addRow([menu.menu]);
      ws1.mergeCells(`A${mr.number}:F${mr.number}`);
      mr.getCell(1).font = { bold: true };
      addBorderAll(mr);

      const predByName = perMenuPredCurr[menu.menu] || {};
      const actByName = perMenuActCurr[menu.menu] || {};

      const names = new Set([
        ...Object.keys(actByName),
        ...Object.keys(predByName),
      ]);

      let totalActualCost = 0;

      for (const name of names) {
        const a = actByName[name] || { qty: 0, unit: "", cost: 0 };
        const p = predByName[name] || { qty: 0, unit: "" };

        totalActualCost += Number(a.cost || 0);

        const row = ws1.addRow([
          "",
          a.unit || p.unit || "",
          name,
          Number(a.qty || 0),
          Number(p.qty || 0),
          Number(a.cost || 0),
        ]);
        row.getCell(4).numFmt = fmtQty;
        row.getCell(5).numFmt = fmtQty;
        row.getCell(6).numFmt = fmtPeso;
        addBorderAll(row);
      }

      const totalCostOut =
        totalActualCost > 0 ? totalActualCost : Number(menu.cost || 0);
      const tr = ws1.addRow(["Total", "", "", "", "", totalCostOut]);
      tr.getCell(1).font = { italic: true };
      tr.getCell(6).numFmt = fmtPeso;
      addBorderAll(tr);

      // blank line
      ws1.addRow([]);
    });

    const ws2 = wb.addWorksheet("Overall Consumption", {
      views: [{ state: "frozen", ySplit: 1 }],
      properties: { defaultColWidth: 15 },
    });

    const header2 = [
      "Inventory",
      "Unit",
      "Previous Qty",
      "Current Qty",
      "Prev Predicted Used",
      "Curr Predicted Used",
      "Δ % (Qty)",
      "Previous Cost (₱)",
      "Current Cost (₱)",
      "Δ % (Cost)",
    ];
    ws2.addRow(header2);
    const headerRow2 = ws2.getRow(1);
    headerRow2.font = { bold: true };
    headerRow2.alignment = { horizontal: "center" };
    headerRow2.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFEFEFEF" },
      };
    });

    inventorySummary.forEach((inv) => {
      const key = inv.inventoryId || inv.name;

      const prevPred = prevAgg.byId[key]?.qty ?? prevAgg.byName[inv.name] ?? 0;
      const currPred = currAgg.byId[key]?.qty ?? currAgg.byName[inv.name] ?? 0;

      const row = ws2.addRow([
        inv.name,
        inv.unit,
        inv.previousQty,
        inv.currentQty,
        prevPred,
        currPred,
        null,
        inv.previousCost,
        inv.currentCost,
        null,
      ]);

      row.getCell(3).numFmt = fmtQty;
      row.getCell(4).numFmt = fmtQty;
      row.getCell(5).numFmt = fmtQty;
      row.getCell(6).numFmt = fmtQty;
      row.getCell(8).numFmt = fmtPeso;
      row.getCell(9).numFmt = fmtPeso;

      const r = row.number;
      row.getCell(7).value = {
        formula: `IF(C${r}=0,"-",ROUND((D${r}-C${r})/C${r}*100,1)/100)`,
      };
      row.getCell(7).numFmt = "0.00%";

      row.getCell(10).value = {
        formula: `IF(H${r}=0,"-",ROUND((I${r}-H${r})/H${r}*100,1)/100)`,
      };
      row.getCell(10).numFmt = "0.00%";

      addBorderAll(row);
    });

    const lastDataRow = ws2.lastRow.number;
    const totalRow = ws2.addRow([
      "TOTAL",
      "",
      "",
      "",
      "",
      "",
      "",
      { formula: `SUM(H2:H${lastDataRow})` },
      { formula: `SUM(I2:I${lastDataRow})` },
      null,
    ]);
    totalRow.font = { bold: true };
    const tr2 = totalRow.number;
    totalRow.getCell(10).value = {
      formula: `IF(H${tr2}=0,"-",ROUND((I${tr2}-H${tr2})/H${tr2}*100,1)/100)`,
    };
    totalRow.getCell(10).numFmt = "0.00%";
    totalRow.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "double" },
        right: { style: "thin" },
      };
    });

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Inventory_Report_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`);
  };

  return (
    <Button variant="contained" color="primary" onClick={handleExport}>
      Export to Excel
    </Button>
  );
}

SalesInventoryExport.propTypes = {
  dateRange: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
};
